import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Grid, InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';
import deepmerge from 'deepmerge';

import SelectField from '../../common/select_field';
import { getAllFoodPartnersState } from '../../../features/food_partner/get_all_food_partners';
import { getConstantsState } from '../../../features/product/get_constants';
import { yesNoOptions } from '../../common/form_utils';
import FilterPaper from '../../common/filter_paper';

const uncompletedLanguageOptions = [
    { value: 'none', label: 'Wszystkie' },
    { value: 'EN', label: 'Brak EN' },
    { value: 'PL', label: 'Brak PL' },
    { value: 'UK', label: 'Brak UK' }
];

const ProductFilters = ({ queryParams, setQueryParams }) => {
    const getConstants = useSelector(getConstantsState);
    const getAllFoodPartners = useSelector(getAllFoodPartnersState);

    // Zaktualizowane stany odpowiadające dokładnie definicji API
    const [uncompletedLanguage, setUncompletedLanguage] = useState(queryParams?.uncompletedLanguage || 'none');
    const [active, setActive] = useState(queryParams?.active || 'none');
    const [foodPartnerId, setFoodPartnerId] = useState(queryParams?.foodPartnerId || 'none');
    const [attributeId, setAttributeId] = useState(queryParams?.attributeId || 'none');
    const [recommended, setRecommended] = useState(queryParams?.recommended || 'none');
    const [defaultPicture, setDefaultPicture] = useState(queryParams?.defaultPicture || 'none');

    const onSearchInputChangeDebounced = _.debounce((query) => setQueryParams(prevState => deepmerge(prevState, { query })), 600);

    const onSelectUncompletedLanguageChange = (event) => {
        const value = event.target.value;
        setUncompletedLanguage(value);

        setQueryParams(prevState => {
            if (value === 'none') {
                const { uncompletedLanguage, ...rest } = prevState;
                return rest;
            }

            return deepmerge(prevState, { uncompletedLanguage: value });
        });
    };

    const onSelectActiveChange = (event) => {
        setActive(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { active: event.target.value }));
    };

    const onSelectFoodPartnerChange = (event) => {
        setFoodPartnerId(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { foodPartnerId: event.target.value }));
    };

    const onSelectAttributeChange = (event) => {
        setAttributeId(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { attributeId: event.target.value }));
    };

    const onSelectRecommendedChange = (event) => {
        setRecommended(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { recommended: event.target.value }));
    };

    const onSelectDefaultPictureChange = (event) => {
        setDefaultPicture(event.target.value);
        setQueryParams(prevState => deepmerge(prevState, { defaultPicture: event.target.value }));
    };

    return (
        <FilterPaper title="Filtry produktów">
            <Grid container spacing={ 3 } alignItems="flex-start">
                <Grid item xs={ 12 } md={ 4 }>
                    <TextField
                        label="Szukaj po nazwie produktu"
                        fullWidth
                        size="small"
                        defaultValue={ queryParams?.query || '' }
                        onChange={ event => onSearchInputChangeDebounced(event.target.value) }
                        InputProps={ {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action"/>
                                </InputAdornment>
                            ),
                        } }
                    />
                </Grid>

                <Grid item xs={ 12 } sm={ 6 } md={ 2 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Aktywny"
                        value={ active }
                        onChange={ onSelectActiveChange }
                        options={ [{ value: 'none', label: 'Wszystkie' }, ...yesNoOptions] }
                    />
                </Grid>

                <Grid item xs={ 12 } sm={ 6 } md={ 2 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Wersje językowe"
                        value={ uncompletedLanguage }
                        onChange={ onSelectUncompletedLanguageChange }
                        options={ uncompletedLanguageOptions }
                    />
                </Grid>

                <Grid item xs={ 12 } sm={ 6 } md={ 4 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Tag"
                        value={ attributeId }
                        onChange={ onSelectAttributeChange }
                        options={ [{
                            value: 'none',
                            label: 'Nie wybrano'
                        }, ...getConstants?.data?.productConstants?.ATTRIBUTE?.map(productConstant => ({
                            value: productConstant?.id,
                            label: productConstant?.body
                        })) || []] }
                    />
                </Grid>

                <Grid item xs={ 12 } md={ 4 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Dostawca"
                        value={ foodPartnerId }
                        onChange={ onSelectFoodPartnerChange }
                        options={ [{
                            value: 'none',
                            label: 'Nie wybrano'
                        }, ...getAllFoodPartners?.data?.map(getFoodPartner => ({
                            value: getFoodPartner?.id,
                            label: getFoodPartner?.name
                        })) || []] }
                    />
                </Grid>

                <Grid item xs={ 12 } sm={ 6 } md={ 2 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Polecany produkt"
                        value={ recommended }
                        onChange={ onSelectRecommendedChange }
                        options={ [{ value: 'none', label: 'Nie wybrano' }, ...yesNoOptions] }
                    />
                </Grid>

                <Grid item xs={ 12 } sm={ 6 } md={ 2 }>
                    <SelectField
                        size="small"
                        fullWidth
                        label="Domyślne zdjęcie"
                        value={ defaultPicture }
                        onChange={ onSelectDefaultPictureChange }
                        options={ [{ value: 'none', label: 'Nie wybrano' }, ...yesNoOptions] }
                    />
                </Grid>
            </Grid>
        </FilterPaper>
    );
};

export default ProductFilters;